import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/app/query-client";
import { getModelCatalog } from "../api/get-model-catalog";
import { listModelPricingRules } from "../api/model-pricing-rules";
import {
  clearPersistedModelCatalog,
  readPersistedModelCatalog,
} from "../api/model-catalog-storage";
import { createBuiltInModelSnapshot } from "./built-in-models";
import { getCapabilityByGenerationMode } from "./generation-mode";
import type {
  AiModel,
  ModelCatalogFilter,
  ModelCatalogSnapshot,
} from "./ai-model";
import type { GenerationMode } from "./generation-mode";
import { compareModelsForMode } from "./model-presentation";

type ModelPricingRulesResponse = Awaited<
  ReturnType<typeof listModelPricingRules>
>;

export const MODEL_CATALOG_STALE_TIME = 15 * 60_000;
export const MODEL_PRICING_RULES_STALE_TIME = 6 * 60 * 60_000;
export const MODEL_PRICING_RULES_GC_TIME = 24 * 60 * 60_000;
export const modelCatalogKeys = {
  all: ["model-catalog"] as const,
  catalog: () => [...modelCatalogKeys.all, "catalog", 5] as const,
  pricingRules: (
    modelId: string | undefined,
    mode: GenerationMode | undefined,
  ) =>
    [
      ...modelCatalogKeys.all,
      "pricing-rules",
      modelId ?? "none",
      mode ?? "none",
    ] as const,
};

export function modelCatalogQueryOptions() {
  const persisted = readPersistedModelCatalog();
  const initialData = persisted ?? createBuiltInModelSnapshot();
  return queryOptions({
    queryKey: modelCatalogKeys.catalog(),
    queryFn: ({ signal }) => getModelCatalog(signal),
    initialData,
    initialDataUpdatedAt: initialData.fetchedAt,
    staleTime: MODEL_CATALOG_STALE_TIME,
    gcTime: 24 * 60 * 60_000,
  });
}

function filterModelCatalog(
  snapshot: ModelCatalogSnapshot,
  filter: ModelCatalogFilter,
): ModelCatalogSnapshot {
  const keyword = filter.keyword?.trim().toLocaleLowerCase("zh-CN");
  const models = snapshot.models.filter((model) => {
    if (filter.capability && !model.capabilities.includes(filter.capability))
      return false;
    if (filter.mediaKind && model.mediaKind !== filter.mediaKind) return false;
    if (filter.status && model.status !== filter.status) return false;
    if (
      keyword &&
      !`${model.name} ${model.provider} ${model.description} ${model.tags.join(" ")}`
        .toLocaleLowerCase("zh-CN")
        .includes(keyword)
    )
      return false;
    return true;
  });
  return { ...snapshot, models };
}

export function useModelCatalog(filter: ModelCatalogFilter = {}) {
  return useQuery({
    ...modelCatalogQueryOptions(),
    select: (snapshot) => filterModelCatalog(snapshot, filter),
  });
}

export function useModelsForMode(mode: GenerationMode) {
  return useQuery({
    ...modelCatalogQueryOptions(),
    select: (snapshot) =>
      snapshot.models
        .filter(
          (model) =>
            model.status !== "maintenance" && model.capabilities.includes(mode),
        )
        .sort((left, right) => compareModelsForMode(left, right, mode)),
  });
}

export function useModelById(modelId: string | undefined) {
  return useQuery({
    ...modelCatalogQueryOptions(),
    select: (snapshot) => snapshot.models.find((model) => model.id === modelId),
  });
}

export function useModelPricingRules(
  modelId: string | undefined,
  mode: GenerationMode | undefined,
) {
  const numericModelId = Number(modelId);
  return useQuery({
    queryKey: modelCatalogKeys.pricingRules(modelId, mode),
    enabled: Number.isSafeInteger(numericModelId) && Boolean(mode),
    staleTime: MODEL_PRICING_RULES_STALE_TIME,
    gcTime: MODEL_PRICING_RULES_GC_TIME,
    queryFn: ({ signal }) =>
      listModelPricingRules(
        {
          modelId: numericModelId,
          capability: getCapabilityByGenerationMode(mode!),
        },
        signal,
      ),
    select: (response) => response.data,
  });
}

export interface ModelPricingSummary {
  modelId: string;
  mode: GenerationMode;
  points: number[];
}

function getDisplayMode(
  model: AiModel,
  preferredMode: GenerationMode | undefined,
) {
  return preferredMode && model.capabilities.includes(preferredMode)
    ? preferredMode
    : model.capabilities[0];
}

function summarizePricingPoints(points: number[]) {
  const uniquePoints = [...new Set(points)].sort((left, right) => left - right);
  if (uniquePoints.length <= 2) return uniquePoints;
  return [uniquePoints[0], uniquePoints[uniquePoints.length - 1]].filter(
    (point): point is number => point !== undefined,
  );
}

export function useModelPricingSummaries(
  models: AiModel[],
  preferredMode: GenerationMode | undefined,
) {
  return useQueries({
    queries: models.map((model) => {
      const mode = getDisplayMode(model, preferredMode);
      const numericModelId = Number(model.id);
      return {
        queryKey: modelCatalogKeys.pricingRules(model.id, mode),
        enabled: Boolean(mode) && Number.isSafeInteger(numericModelId),
        staleTime: MODEL_PRICING_RULES_STALE_TIME,
        gcTime: MODEL_PRICING_RULES_GC_TIME,
        queryFn: ({ signal }) =>
          listModelPricingRules(
            {
              modelId: numericModelId,
              capability: getCapabilityByGenerationMode(mode!),
            },
            signal,
          ),
        select: (response: ModelPricingRulesResponse) => ({
          modelId: model.id,
          mode: mode!,
          points: summarizePricingPoints(
            response.data.map((rule) => rule.points),
          ),
        }),
      };
    }),
    combine: (results) => {
      const summaries = new Map<string, ModelPricingSummary>();
      results.forEach((result) => {
        if (result.data) summaries.set(result.data.modelId, result.data);
      });
      return {
        summaries,
        isFetching: results.some((result) => result.isFetching),
      };
    },
  });
}

export function prefetchModelCatalog() {
  return queryClient.prefetchQuery(modelCatalogQueryOptions());
}

export function getCachedModelCatalog() {
  return queryClient.getQueryData<ModelCatalogSnapshot>(
    modelCatalogKeys.catalog(),
  );
}

export function getCachedModel(modelId: string) {
  return getCachedModelCatalog()?.models.find((model) => model.id === modelId);
}

export function setCachedModelCatalog(snapshot: ModelCatalogSnapshot) {
  queryClient.setQueryData(modelCatalogKeys.catalog(), snapshot);
}

export async function invalidateModelCatalog() {
  clearPersistedModelCatalog();
  await queryClient.invalidateQueries({ queryKey: modelCatalogKeys.all });
}

export function selectRecommendedModel(models: AiModel[]) {
  return models.find((model) => model.recommended) ?? models[0];
}
